/**
 * CountUp — animates an integer from 0 → `value` once it scrolls into view, using
 * framer-motion's animation engine. Respects prefers-reduced-motion: when the user
 * prefers reduced motion the final value is rendered immediately with no tween.
 *
 * Used by the hero stats row. The displayed number is formatted with thousands
 * separators and an optional prefix/suffix so values like "20,673" and "10K+"
 * render correctly.
 */
import * as React from 'react';
import {
  animate,
  useInView,
  useReducedMotion,
} from 'framer-motion';

export interface CountUpProps {
  /** Target value to count up to. */
  value: number;
  /** Tween duration in seconds. */
  duration?: number;
  /** Static text rendered before the number (e.g. nothing, or a currency mark). */
  prefix?: string;
  /** Static text rendered after the number (e.g. "%" or "K+"). */
  suffix?: string;
  /** When true, formats with en-US thousands separators. */
  separator?: boolean;
  /** Fixed decimal places (e.g. 1 for "815.3M+"). Defaults to 0 (integers). */
  decimals?: number;
  className?: string;
}

const CountUp: React.FC<CountUpProps> = ({
  value,
  duration = 1.4,
  prefix = '',
  suffix = '',
  separator = true,
  decimals = 0,
  className,
}) => {
  const reduceMotion = useReducedMotion();
  const ref = React.useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  const format = React.useCallback(
    (n: number) => {
      const opts = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
      const body = separator
        ? n.toLocaleString('en-US', opts)
        : n.toFixed(decimals);
      return `${prefix}${body}${suffix}`;
    },
    [prefix, suffix, separator, decimals],
  );

  const [display, setDisplay] = React.useState<string>(() =>
    reduceMotion ? format(value) : format(0),
  );

  React.useEffect(() => {
    if (reduceMotion) {
      setDisplay(format(value));
      return;
    }
    if (!inView) return;

    const controls = animate(0, value, {
      duration,
      ease: 'easeOut',
      onUpdate: (latest) => setDisplay(format(latest)),
    });
    return () => controls.stop();
  }, [inView, value, duration, reduceMotion, format]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default CountUp;
